import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { AccordionGroupChangeEventDetail } from '@ionic/react';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useMenuSearchShortcut } from '../../../hooks/shortcuts/useMenuSearchShortcut';
import { OpenMenu } from '../../../types/types';
import { filterMenuItems } from '../utils/filterMenuItems';
import { normalizeAccordionValue } from '../utils/normalizeAccordionValue';
import { normalizeSearchQuery } from '../utils/normalizeSearchQuery';
import { useStableStringArray } from './useStableStringArray';

type UseMenuSearchProps = {
  menuItems: any[];
  // Which block/submenu is open when not searching (driven by the active route).
  openMenu: OpenMenu;
  // Root element used by the keyboard shortcut to know typing belongs to the menu.
  scopeRef: RefObject<HTMLElement | null>;
  // On the dashboard, typing anywhere opens the menu search (not just when the menu is focused).
  activateAnywhere?: boolean;
};

// Owns the side-menu search: the query, the filtered tree, which accordions to open, and the
// type-to-search shortcut. Keeps Sidebar free of filtering logic.
export const useMenuSearch = ({
  menuItems,
  openMenu,
  scopeRef,
  activateAnywhere,
}: UseMenuSearchProps) => {
  const { t } = useTranslate();
  const { pathname } = useLocation();
  // Search text. Starts empty and never persists across pages.
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const prevPathnameRef = useRef(pathname);
  const normalizedQuery = normalizeSearchQuery(searchQuery);
  const isSearching = normalizedQuery.length > 0;
  // "Overview" is a fixed entry, so match it against the query separately.
  const overviewMatches =
    !isSearching || t('overview').toLowerCase().includes(normalizedQuery);

  const filteredMenuItems = useMemo(
    () => filterMenuItems(menuItems, searchQuery),
    [menuItems, searchQuery]
  );

  // User-toggled open keys while searching; null = derive the open set from the filtered tree.
  const [searchOpenBlockKeys, setSearchOpenBlockKeys] = useState<
    string[] | null
  >(null);
  const [searchOpenSubmenuKeys, setSearchOpenSubmenuKeys] = useState<
    string[] | null
  >(null);

  // Every query change re-derives the open set, dropping manual toggles.
  const prevQueryRef = useRef(normalizedQuery);
  if (prevQueryRef.current !== normalizedQuery) {
    prevQueryRef.current = normalizedQuery;
    setSearchOpenBlockKeys(null);
    setSearchOpenSubmenuKeys(null);
  }

  useMenuSearchShortcut({
    scopeRef,
    inputRef: searchInputRef,
    shortcutId: 'side-menu-search',
    activateAnywhere,
  });

  // Keys of the accordions to keep open. While searching: every matching block/submenu. Otherwise: the
  // active one. Always arrays (even for a single key) so the accordion groups can stay `multiple` — see
  // Sidebar. useStableStringArray keeps the reference steady while the keys don't change.
  const openBlockKeys = useStableStringArray(
    useMemo<string[]>(() => {
      if (!isSearching) {
        return typeof openMenu[0] === 'string' ? [openMenu[0]] : [];
      }
      if (searchOpenBlockKeys !== null) {
        return searchOpenBlockKeys;
      }
      return filteredMenuItems
        .map((block) => block.key)
        .filter((key): key is string => typeof key === 'string');
    }, [isSearching, filteredMenuItems, openMenu, searchOpenBlockKeys])
  );

  const openSubmenuKeys = useStableStringArray(
    useMemo<string[]>(() => {
      if (!isSearching) {
        return typeof openMenu[1] === 'string' ? [openMenu[1]] : [];
      }
      if (searchOpenSubmenuKeys !== null) {
        return searchOpenSubmenuKeys;
      }
      return filteredMenuItems
        .flatMap((block) => block.children ?? [])
        .filter((item) => item.type === 'submenu')
        .map((item) => item.key)
        .filter((key): key is string => typeof key === 'string');
    }, [isSearching, filteredMenuItems, openMenu, searchOpenSubmenuKeys])
  );

  // Mirror user toggles into state, otherwise the next render re-applies the stale full list and a closed section can't reopen.
  const onSearchBlocksChange = useCallback(
    (
      event: CustomEvent<AccordionGroupChangeEventDetail<string | string[]>>
    ) => {
      // Nested submenu groups bubble their ionChange through the block group.
      if (!isSearching || event.target !== event.currentTarget) {
        return;
      }
      setSearchOpenBlockKeys(normalizeAccordionValue(event.detail.value));
    },
    [isSearching]
  );

  const onSearchSubmenusChange = useCallback(
    (
      event: CustomEvent<AccordionGroupChangeEventDetail<string | string[]>>
    ) => {
      if (!isSearching || event.target !== event.currentTarget) {
        return;
      }
      setSearchOpenSubmenuKeys(normalizeAccordionValue(event.detail.value));
    },
    [isSearching]
  );

  const resetSearch = useCallback(() => setSearchQuery(''), []);

  // Clear the search whenever the route changes.
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      setSearchQuery('');
    }
  }, [pathname]);

  return {
    searchQuery,
    setSearchQuery,
    resetSearch,
    isSearching,
    overviewMatches,
    filteredMenuItems,
    openBlockKeys,
    openSubmenuKeys,
    onSearchBlocksChange,
    onSearchSubmenusChange,
    searchInputRef,
  };
};
